import { memo, useMemo, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconDirectionDown } from '@components/icons/direction-down';
import { IconGear } from '@components/icons/gear';
import { IconWorkspace } from '@components/icons/workspace';
import { PillTabs } from '@components/pill-tabs';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { JourneyCard } from './journey-card';
import { JourneyCardList } from './journey-card-list';
import { JourneyListEmpty, JourneyListError, JourneyListLoading } from './list-states';
import { RawButton } from './raw-button';
import { JourneyListRow } from './types';

type FilterTab = 'all' | 'enabled' | 'disabled';
const FILTER_TABS: FilterTab[] = ['all', 'enabled', 'disabled'];

export type JourneyListSectionLabels = {
  all: string;
  enabled: string;
  disabled: string;
  searchPlaceholder: string;
  /** Shown when there are no journeys at all (empty list) */
  noJourneys: string;
  /** Shown when the search/filter yields no matches but journeys exist */
  noResults: string;
  errorDescription: string;
  /** Label for the retry button shown on the error state. */
  retry: string;
  configure: string;
  toggleEnabledLabel: string;
  toggleDisabledLabel: string;
  /** Shown above the list when the server-side catalog was capped. */
  showingFirstResults: string;
};

export type JourneyListSectionProps = {
  /** Already search-filtered by the server — this component no longer filters by search. */
  journeys: JourneyListRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** True when the upstream catalog was truncated at the cap. */
  isTruncated?: boolean;
  /** Current search box value — owned by the parent so it can drive the query. */
  searchValue: string;
  /** Reports the search box's (debounced) value upward. */
  onSearchChange: (search: string) => void;
  onToggle: (journey: JourneyListRow, newIsEnabled: boolean) => void;
  onConfigure: (journey: JourneyListRow) => void;
  /** Refetches the owning query — wired to the error state's Retry button. */
  onRetry: () => void;
  /**
   * journeyId of the row whose enable-toggle is in flight, if any. Only that
   * row's switch is disabled — a slow request must not freeze every toggle.
   */
  mutatingJourneyId: string | null | undefined;
  labels: JourneyListSectionLabels;
  /** data-cy placed on each journey row card */
  journeyRowCy: string;
  /** data-cy placed on the toggle switch inside each row */
  toggleCy: string;
  /**
   * data-testid placed on the search box. SearchBox only supports dataTestid,
   * so this one hook stays data-testid while every other hook here is data-cy.
   */
  searchTestid: string;
  /** data-cy placed on the loading container */
  loadingCy?: string;
  /** data-cy placed on the error container */
  errorCy?: string;
};

type JourneyRowProps = {
  journey: JourneyListRow;
  journeyRowCy: string;
  toggleCy: string;
  toggleEnabledLabel: string;
  toggleDisabledLabel: string;
  configureLabel: string;
  isMutating: boolean;
  onToggle: (journey: JourneyListRow, newIsEnabled: boolean) => void;
  onConfigure: (journey: JourneyListRow) => void;
};

/**
 * A single journey row (card + enable switch + configure button).
 *
 * Module-level and memoized so that re-rendering the list for an unrelated
 * reason (filter-tab switch, a sibling workspace collapsing) does not also
 * re-render every row — only the row(s) whose own props actually changed.
 * That only pays off because the handlers below are read out of `journey`
 * inside the row itself rather than being newly bound per-row in the parent's
 * `.map()`, so `onToggle`/`onConfigure` can stay the same stable reference the
 * parent passes down.
 */
const JourneyRow = memo(function JourneyRow({
  journey,
  journeyRowCy,
  toggleCy,
  toggleEnabledLabel,
  toggleDisabledLabel,
  configureLabel,
  isMutating,
  onToggle,
  onConfigure,
}: JourneyRowProps) {
  return (
    <JourneyCard
      dataCy={journeyRowCy}
      title={journey.title}
      description={journey.description}
      fieldsCount={journey.fieldsCount}
      preFilledCount={journey.preFilledCount}
      isMuted={!journey.isEnabled}
      actions={
        <>
          {/* ListCard's actions row (`ActionContainer` in @components/list-card)
              never sets `align`, so it falls back to the flex default
              `align-items: stretch`. The Configure button has a fixed 32px
              height, so it stretches this row to 32px; Switch's own outer
              wrapper is a *vertical* Stack with no `justify`, so its 24px
              toggle doesn't re-center inside the extra height — it stays
              pinned to the top. `alignSelf: 'center'` opts this one flex
              item out of the inherited stretch instead of stretching-then-
              repinning, so the toggle sits at its natural height, centered
              against the Configure button. */}
          <ToggleAlign data-testid="journey-toggle-align">
            <Switch
              dataCy={toggleCy}
              value={journey.isEnabled}
              label={journey.isEnabled ? toggleEnabledLabel : toggleDisabledLabel}
              labelVisible={false}
              onChange={(isEnabled) => onToggle(journey, isEnabled)}
              isDisabled={isMutating}
            />
          </ToggleAlign>
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<IconGear />}
            text={configureLabel}
            dataCy="journey-configure"
            onClick={() => onConfigure(journey)}
          />
        </>
      }
    />
  );
});

/**
 * Shared workspace-grouped journey list with search, enabled/disabled filter
 * tabs, and collapsible workspace sections.
 *
 * Used by both the global Manage tab and the per-job Journeys tab so both
 * screens evolve together instead of drifting. Search is server-side —
 * `journeys` arrives already filtered by `searchValue`; this component only
 * applies the enabled/disabled tab filter and the workspace grouping.
 */
export const JourneyListSection = ({
  journeys: journeyData,
  isLoading,
  isError,
  isTruncated,
  searchValue,
  onSearchChange,
  onToggle,
  onConfigure,
  onRetry,
  mutatingJourneyId,
  labels,
  journeyRowCy,
  toggleCy,
  searchTestid,
  loadingCy,
  errorCy,
}: JourneyListSectionProps) => {
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [collapsedWorkspaces, setCollapsedWorkspaces] = useState<Set<string>>(new Set());

  const journeys = useMemo(() => journeyData ?? [], [journeyData]);

  const filteredJourneys = useMemo(() => {
    if (filterTab === 'all') return journeys;
    return journeys.filter((journey) =>
      filterTab === 'enabled' ? journey.isEnabled : !journey.isEnabled,
    );
  }, [journeys, filterTab]);

  const groupedByWorkspace = useMemo(() => {
    const groups: Record<
      string,
      { workspaceId: string; title: string; journeys: JourneyListRow[] }
    > = {};
    for (const journey of filteredJourneys) {
      const key = journey.workspaceId;
      if (!groups[key]) {
        groups[key] = { workspaceId: key, title: journey.workspaceTitle, journeys: [] };
      }
      groups[key].journeys.push(journey);
    }
    return Object.values(groups).sort((groupA, groupB) =>
      (groupA.title ?? '').localeCompare(groupB.title ?? ''),
    );
  }, [filteredJourneys]);

  // One pass over `journeys` for both counts, instead of two separate
  // .filter().length passes.
  const { enabledCount, disabledCount } = useMemo(() => {
    let enabled = 0;
    let disabled = 0;
    for (const journey of journeys) {
      if (journey.isEnabled) {
        enabled += 1;
      } else {
        disabled += 1;
      }
    }
    return { enabledCount: enabled, disabledCount: disabled };
  }, [journeys]);

  const toggleWorkspaceCollapsed = (workspaceId: string) => {
    setCollapsedWorkspaces((previous) => {
      const next = new Set(previous);
      if (next.has(workspaceId)) {
        next.delete(workspaceId);
      } else {
        next.add(workspaceId);
      }
      return next;
    });
  };

  // journeys is already search-filtered by the server, so an empty result
  // only means "no journeys at all" when neither a search nor the tab filter
  // is narrowing it — otherwise it means "nothing matched".
  const isNarrowed = Boolean(searchValue.trim()) || filterTab !== 'all';

  return (
    <Stack space={5}>
      {/* The search box (and the truncation notice) must survive a new search
          loading and a failed search — otherwise the user loses their typed
          query, and their only way to retry a search is the unrelated Retry
          button. So both are mounted unconditionally, above the
          loading/error/empty/list ladder below. */}
      <SearchBox
        dataTestid={searchTestid}
        placeholder={labels.searchPlaceholder}
        defaultValue=""
        onChange={onSearchChange}
        onChangeDebounce={300}
      />

      {isTruncated && (
        <Text font="regular12" color="textHelper" text={labels.showingFirstResults} />
      )}

      {isLoading ? (
        <JourneyListLoading dataCy={loadingCy} />
      ) : isError ? (
        <JourneyListError
          description={labels.errorDescription}
          retryLabel={labels.retry}
          onRetry={onRetry}
          dataCy={errorCy}
        />
      ) : (
        <>
          <PillTabs
            size="sm"
            selectedIndex={FILTER_TABS.indexOf(filterTab)}
            onChange={(index) => setFilterTab(FILTER_TABS[index])}
          >
            <PillTabs.Tabs>
              <PillTabs.Tab label={labels.all} count={journeys.length} />
              <PillTabs.Tab label={labels.enabled} count={enabledCount} />
              <PillTabs.Tab label={labels.disabled} count={disabledCount} />
            </PillTabs.Tabs>
          </PillTabs>

          {groupedByWorkspace.length === 0 ? (
            <JourneyListEmpty
              text={journeys.length === 0 && !isNarrowed ? labels.noJourneys : labels.noResults}
            />
          ) : (
            <Stack space={6}>
              {groupedByWorkspace.map(
                ({ workspaceId, title: workspaceTitle, journeys: workspaceJourneys }) => {
                  const isCollapsed = collapsedWorkspaces.has(workspaceId);
                  const journeyListId = `workspace-journeys-${workspaceId}`;
                  return (
                    <div key={workspaceId} data-cy="workspace-section">
                      <Stack space={3}>
                        <WorkspaceHeader
                          type="button"
                          onClick={() => toggleWorkspaceCollapsed(workspaceId)}
                          aria-expanded={!isCollapsed}
                          aria-controls={journeyListId}
                        >
                          <IconWorkspace size={16} color="orange100" />
                          <Text font="semibold12" text={workspaceTitle} />
                          <Text
                            font="regular12"
                            color="textHelper"
                            text={String(workspaceJourneys.length)}
                            dataTestid="workspace-count"
                          />
                          <ChevronIcon className={isCollapsed ? 'collapsed' : ''}>
                            <IconDirectionDown size={16} color="textHelper" />
                          </ChevronIcon>
                        </WorkspaceHeader>
                        <div id={journeyListId} hidden={isCollapsed}>
                          <JourneyCardList
                            items={workspaceJourneys}
                            getKey={(journey) => journey.journeyId}
                            renderItem={(journey) => (
                              <JourneyRow
                                journey={journey}
                                journeyRowCy={journeyRowCy}
                                toggleCy={toggleCy}
                                toggleEnabledLabel={labels.toggleEnabledLabel}
                                toggleDisabledLabel={labels.toggleDisabledLabel}
                                configureLabel={labels.configure}
                                isMutating={mutatingJourneyId === journey.journeyId}
                                onToggle={onToggle}
                                onConfigure={onConfigure}
                              />
                            )}
                          />
                        </div>
                      </Stack>
                    </div>
                  );
                },
              )}
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

const ToggleAlign = styled.div({
  alignSelf: 'center',
});

const WorkspaceHeader = styled(RawButton)({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
  background: 'none',
  padding: 0,
  transition: transitionStandard('opacity'),
  '&:hover': {
    opacity: 0.7,
  },
});

const ChevronIcon = styled.span({
  display: 'flex',
  transition: transitionStandard('transform'),
  '&.collapsed': {
    transform: 'rotate(-90deg)',
  },
});
